import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Spin,
  List,
  Empty,
  AutoComplete,
  Tag,
  Select,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  PieChart,
  Pie,
  Cell,
  Line,
  LineChart,
  CartesianGrid,
} from "recharts";
import useSkillsReports from "./logic/useSkillsReports";
import "./SkillsReports.scss";
import SkillMatrixByUser from "./components/SkillMatrixByUser";
import debounce from "lodash/debounce";
import useSearchUsersBySkill from "./logic/useSearchUsersBySkill";
import useSearchSkillsByName from "./logic/useSearchSkillsByName";
import { Pagination } from "antd";

const LEVEL_COLORS = {
  L0: "#90caf9",
  L1: "#42a5f5",
  L2: "#ff8042",
  L3: "#8884d8",
  L4: "#ef5350",
};
const PIE_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff8042",
  "#a25eff",
  "#f06292",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: 10,
          border: "1px solid #ccc",
        }}
      >
        <strong>{label}</strong>
        {payload.map((entry, index) => (
          <div key={index} style={{ color: entry.color }}>
            {entry.name}:{" "}
            {entry.payload[entry.dataKey.replace("Percent", "Count")]} users (
            {entry.payload[entry.dataKey]}%)
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const SkillsReports = () => {
  const {
    rawTopSkillsData,
    loadingTopSkills,
    skillLevelsDistribution,
    loadingSkillLevels,
    skillDistributionByCategory,
    loadingSkillDistribution,
    skillCategories,
    loadingSkillCategories,
    departments,
    loadingDepartments,
    skillAcquisitionProgress,
    loadingSkillAcquisition,
    fetchSkillLevelsByCategoriesAndTeams,
    manualRequestsStats,
    certifiedRequestsStats,
    loadingManualStats,
    loadingCertifiedStats,
    manualRequestsByDept,
    certifiedRequestsByDept,
    loadingManualByDept,
    loadingCertifiedByDept,
  } = useSkillsReports();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [filteredLevels, setFilteredLevels] = useState(skillLevelsDistribution);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");
  const [submittedSkill, setSubmittedSkill] = useState("");
  const { data: skillOptions = [] } = useSearchSkillsByName(skillSearch);
  const [matrixSkillSearch, setMatrixSkillSearch] = useState("");
  const [userSkillSearch, setUserSkillSearch] = useState("");
  const skillProgress = (skillAcquisitionProgress || []).map((item) => ({
    month: String(item.month),
    skills: Number(item.skillCount) || 0,
  }));

  const handleClearMatrix = () => {
    setMatrixSkillSearch("");
  };

  const handleClearUser = () => {
    setSubmittedSkill("");
    setSkillSearch("");
  };

  const handleSkillSearch = debounce((value) => {
    setSkillSearch(value);
  }, 300);

  const handleSkillSelect = (value) => {
    setSubmittedSkill(value);
  };
  const { data: usersBySkill, isLoading: loadingUsersBySkill } =
    useSearchUsersBySkill(submittedSkill);

  useEffect(() => {
    const fetchFiltered = async () => {
      if (selectedCategories.length === 0 && selectedDepartments.length === 0) {
        setFilteredLevels(skillLevelsDistribution);
        return;
      }

      setLoadingFiltered(true);

      try {
        const filteredData = await fetchSkillLevelsByCategoriesAndTeams(
          selectedCategories,
          selectedDepartments
        );
        setFilteredLevels(filteredData);
      } catch (error) {
        console.error("Error fetching filtered skill levels:", error);
      }

      setLoadingFiltered(false);
    };

    fetchFiltered();
  }, [selectedCategories, selectedDepartments, skillLevelsDistribution]);

  const allSkillNames = [
    ...new Set(
      rawTopSkillsData.flatMap((dept) =>
        dept.topSkills.map((skill) => skill.skillName)
      )
    ),
  ];

  const transformedData = rawTopSkillsData.map((dept) => {
    const skillObj = { department: dept.department };
    dept.topSkills.forEach((skill) => {
      skillObj[skill.skillName] = skill.userCount;
    });
    return skillObj;
  });

  const transformedLevelData = filteredLevels.map((item) => ({
    skillName: item.skillName,
    L0Count: item.level0.count,
    L0Percent: parseFloat(item.level0.percentage),
    L1Count: item.level1.count,
    L1Percent: parseFloat(item.level1.percentage),
    L2Count: item.level2.count,
    L2Percent: parseFloat(item.level2.percentage),
    L3Count: item.level3.count,
    L3Percent: parseFloat(item.level3.percentage),
    L4Count: item.level4.count,
    L4Percent: parseFloat(item.level4.percentage),
  }));

  const levelKeys = ["L0", "L1", "L2", "L3", "L4"];

  const safeText = (value, fallback = "Unknown") => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      String(value).toLowerCase().trim() === "null" ||
      String(value).toLowerCase().trim() === "undefined"
    ) {
      return fallback;
    }
    return value;
  };
  const transformRequestStatsToPieData = (stats) => {
    if (!stats || Object.keys(stats).length === 0) return [];

    return [
      {
        name: "Approved",
        value: parseFloat(stats.approved),
        count: stats.approvedRequests,
      },
      {
        name: "Pending",
        value: parseFloat(stats.pending),
        count: stats.pendingRequests,
      },
      {
        name: "Rejected",
        value: parseFloat(stats.rejected),
        count: stats.rejectedRequests,
      },
    ];
  };
  const CustomRequestTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const { name, value, payload: fullData } = payload[0];
      return (
        <div
          style={{
            background: "#fff",
            padding: 10,
            border: "1px solid #ccc",
          }}
        >
          <strong>{name}</strong>
          <div>{`Count: ${fullData.count}`}</div>
          <div>{`Percentage: ${value}%`}</div>
        </div>
      );
    }
    return null;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 14;

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = transformedLevelData.slice(startIndex, endIndex);

  return (
    <div className="skills-reports-container">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Top Skills by Team" className="chart-card">
            <ResponsiveContainer width="100%" height={400}>
              {loadingTopSkills ? (
                <Spin size="large" />
              ) : (
                <BarChart data={transformedData}>
                  <XAxis dataKey="department" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {allSkillNames.map((skill, index) => (
                    <Bar
                      key={skill}
                      dataKey={skill}
                      fill={LEVEL_COLORS[`L${index % 5}`]}
                      name={skill}
                    />
                  ))}
                </BarChart>
              )}
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
      <Row justify="end" gutter={[16, 16]} style={{ margin: "16px 0" }}>
        <Col span={6}>
          {loadingSkillCategories ? (
            <Spin />
          ) : (
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by categories"
              style={{ width: "100%" }}
              value={selectedCategories}
              onChange={setSelectedCategories}
            >
              {skillCategories.map((cat) => (
                <Select.Option key={cat} value={cat}>
                  {cat}
                </Select.Option>
              ))}
            </Select>
          )}
        </Col>

        <Col span={6}>
          {loadingDepartments ? (
            <Spin />
          ) : (
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by teams"
              style={{ width: "100%" }}
              value={selectedDepartments}
              onChange={setSelectedDepartments}
            >
              {departments.map((dep) => (
                <Select.Option key={dep} value={dep}>
                  {dep}
                </Select.Option>
              ))}
            </Select>
          )}
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="charts-row">

        {/* Pie Chart */}
        <Col span={8} className="charts-col">
          <Card title="Skill Categories Distribution" className="chart-card">
            {loadingSkillDistribution ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={500}>
                <PieChart>
                  <Pie
                    data={skillDistributionByCategory}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={({ name, percent, x, y, index }) => (
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                          fontSize: 12,
                          fill: PIE_COLORS[index % PIE_COLORS.length],
                        }}
                      >
                        {`${name} ${(percent * 100).toFixed(1)}%`}
                      </text>
                    )}
                  >
                    {skillDistributionByCategory.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => {
                      const { payload } = props;
                      return [`${value} skills (${payload.percentage}%)`, name];
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        {/* Bar Chart Niveau */}
         <Col span={16} className="charts-col">
               <Card title="Skill Levels Distribution" className="chart-card">
                 {loadingSkillLevels || loadingFiltered ? (
                   <Spin />
                 ) : (
                   <>
                     <ResponsiveContainer width="100%" height={ITEMS_PER_PAGE * 35}>
                       <BarChart layout="vertical" data={paginatedData}>
                         <XAxis
                           type="number"
                           domain={[0, 100]}
                           tickFormatter={(tick) => `${tick}%`}
                         />
                         <YAxis
                           type="category"
                           dataKey="skillName"
                           width={200}
                           tick={{ fontSize: 12 }}
                         />
                         <Tooltip content={<CustomTooltip />} />
                         <Legend />
                         {["L0", "L1", "L2", "L3", "L4"].map((level) => (
                           <Bar
                             key={level}
                             dataKey={`${level}Percent`}
                             stackId="a"
                             fill={LEVEL_COLORS[level]}
                             name={`Level ${level.replace("L", "")}`}
                           >
                             <LabelList
                               dataKey={`${level}Percent`}
                               formatter={(val) => (val > 5 ? `${val}%` : "")}
                               position="inside"
                             />
                           </Bar>
                         ))}
                       </BarChart>
                     </ResponsiveContainer>
     
                     <div
                       style={{
                         display: "flex",
                         justifyContent: "center",
                         marginTop: 20,
                       }}
                     >
                       <Pagination
                         current={currentPage}
                         pageSize={ITEMS_PER_PAGE}
                         total={transformedLevelData.length}
                         onChange={(page) => setCurrentPage(page)}
                         showSizeChanger={false}
                       />
                     </div>
                   </>
                 )}
               </Card>
             </Col>
      </Row>
      <Row gutter={[16, 16]} className="skillsMatrix-row">
        <SkillMatrixByUser />
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 32 }}>
        <Col span={12}>
          <Card title="Search Users by Skill" className="search-users-card">
            <AutoComplete
              style={{ maxWidth: 400, marginBottom: 16 }}
              options={skillOptions.map((s) => ({ value: s.name }))}
              placeholder="Enter a skill name"
              onSearch={handleSkillSearch}
              onSelect={handleSkillSelect}
              onClear={handleClearUser}
              value={skillSearch}
              onChange={(value) => {
                setSkillSearch(value);
                if (value === "") handleClearUser();
              }}
              allowClear
            />

            {submittedSkill && usersBySkill && usersBySkill.length > 0 ? (
              <List
                bordered
                dataSource={usersBySkill}
                renderItem={(item) => (
                  <List.Item>
                    <div className="user-skill-info">
                      <div className="user-informations">
                        {safeText(item?.userName, "Unknown User")}
                      </div>
                      <div className="user-informations">
                        {safeText(item?.occupation, "-")}
                      </div>
                      <div className="user-informations">
                        {safeText(item?.team, "Unknown team")}
                      </div>
                      <div className="user-tags">
                        <Tag color="purple">
                          L
                          {item?.level !== null && item?.level !== undefined
                            ? item.level
                            : "-"}
                        </Tag>
                        <Tag color="blue">
                          {safeText(item?.source, "Unknown source")}
                        </Tag>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            ) : submittedSkill && !loadingUsersBySkill ? (
              <Empty description="No users found with this skill" />
            ) : null}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Skill Acquisition Progress">
            {loadingSkillAcquisition ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={skillProgress}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="skills"
                    stroke="#82ca9d"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 32 }}>
        <Col span={12}>
          <Card title="Manual Skill Requests Stats">
            {loadingManualStats ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={transformRequestStatsToPieData(manualRequestsStats)}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {["#8884d8", "#82ca9d", "#ff6f61"].map((color, i) => (
                      <Cell key={`cell-${i}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomRequestTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Certified Skill Requests Stats">
            {loadingCertifiedStats ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={transformRequestStatsToPieData(
                      certifiedRequestsStats
                    )}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {["#8884d8", "#82ca9d", "#ff6f61"].map((color, i) => (
                      <Cell key={`cell-${i}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomRequestTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 32 }}>
        <Col span={12}>
          <Card title="Manual Requests by Team">
            {loadingManualByDept ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={manualRequestsByDept}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pending" fill="#8884d8" name="Pending" />
                  <Bar dataKey="approved" fill="#82ca9d" name="Approved" />
                  <Bar dataKey="rejected" fill="#ff6f61" name="Rejected" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Certified Requests by Team">
            {loadingCertifiedByDept ? (
              <Spin size="large" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={certifiedRequestsByDept}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pending" fill="#8884d8" name="Pending" />
                  <Bar dataKey="approved" fill="#82ca9d" name="Approved" />
                  <Bar dataKey="rejected" fill="#ff6f61" name="Rejected" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SkillsReports;
